import { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import { multiPolygon, points } from '@turf/helpers';
import pointsWithinPolygon from '@turf/points-within-polygon';
import { Position } from 'deck.gl';
import { MarkerItem, overlay } from '../../../../types/home';
import Api from '../../../../api/Api';
import { DATASET_CATEGORIES } from '../../../../utils/filterBestDataset';
import { withLoading } from '../../../common/Loading/withLoading';
import LoadingContext from '../../../../context/loadingContext/LoadingContext';
import { useMarkerStore } from '../../../../store/markerStore';
import {THOWDWAVESPECTRA} from "../../../../utils/common";

export const useDatasetSpectraFeature = (
  coords: number[],
  datasetId: string,
  label: string
) => {
  const [show2DSpectra, setShow2DSpectra] = useState(false);
  const loadingContext = useContext(LoadingContext);
  const point = useMarkerStore(state => state.activeMarker);

  const findLabel = useCallback((
    shapeFiles: MarkerItem['shapeFiles'] | undefined,
    label: string
  ) => {
    if (!shapeFiles) return false;
    return shapeFiles.some((shapeFile) => shapeFile.Level2 === label);
  }, []);

  const has2DSpectra = useMemo(() => {
    return (
      findLabel(point?.shapeFiles, THOWDWAVESPECTRA) &&
      label === DATASET_CATEGORIES.WAVES
    );
  }, [point?.shapeFiles, datasetId, coords, findLabel, label]);

  const getGeoJson = useCallback(async (
    shape: overlay,
    options: { isMounted?: { current: boolean } } = {}
  ) => {
    const { isMounted } = options;

    if (!point?.dataset) return;

    if (shape && shape?.data === undefined) {
      shape.data = await Api.fileEndPoint.getOverlayGeoJson(
        point.dataset,
        shape.Level1,
        shape.Level2,
        shape.LegendColor[0]
      );
    }

    if (isMounted?.current) {
      if (shape.data) {
        const polys = shape.data.map(poly => [poly.coordinates as Position[]]);
        const multipolygon = multiPolygon(polys);
        const activePoint = points([coords]);
        const pointsWithin = pointsWithinPolygon(activePoint, multipolygon);
        setShow2DSpectra(pointsWithin && pointsWithin.features && pointsWithin.features.length > 0);
      } else {
        setShow2DSpectra(false);
      }
    }
  }, [coords, point?.dataset]);

  useEffect(() => {
    setShow2DSpectra(false);

    if (has2DSpectra && point) {
      const shape = point.shapeFiles.find(
        (x) => x.Level2 === THOWDWAVESPECTRA
      );

      if (shape) {
        const isMounted = { current: true };
        withLoading(() => getGeoJson(shape, { isMounted }), loadingContext, {
          source: 'useSpectraFeatures - getGeoJson'
        });

        return () => {
          isMounted.current = false;
        };
      }
    }
  }, [point?.shapeFiles, has2DSpectra, coords]);

  return {
    has2DSpectra,
    show2DSpectra
  };
};
